import { useState, useCallback, useRef } from 'react';
import { FileDocumentType } from '@nkr/enums/document-type';
import { ITripGroup } from '../types/trip.types';
import { ILineItem } from '../types/line-item.types';
import { sortLineItemsBySummaryOrder, sumMileageLineItemAmount, sumMileageRoundTripMiles } from '../utils/expense.utils';
import { ExpenseType, MILEAGE_REVIEW_KEY } from '../constants/expense-types';

interface UseSidebarStateProps {
    receiptFileUrls: Map<number, string>;
    fetchFileUrl: (fileId: number) => Promise<string | null>;
    aggregateReceiptFiles: (lineItems: ILineItem[], tripId?: number, tripFileIds?: number[], loadFirstOnly?: boolean) => Promise<Map<number, string>>;
    onShowDataSideBar: (
        docType: FileDocumentType,
        fileId: number,
        fileUrl: string,
        totalFiles: number,
        fileIds: number[],
        currentIndex: number,
        fileIdToLineItemsMap: Map<number, ILineItem[]>,
        onApproveHandler?: (lineItemIds: number[]) => Promise<void>,
        onRequestReviewHandler?: (lineItemIds: number[]) => Promise<void>,
        handleExpenseTypeChange?: (expenseType: number | null, fileIndex?: number) => void,
        handleExpenseTypeApproved?: (expenseType: number) => void,
        refreshLineItems?: () => Promise<void>,
        handleReviewRequiredStateChange?: (lineItemIds: number[], reviewRequired: boolean) => void
    ) => void;
    onSetReviewRequiredBulk?: (lineItemIds: number[], reviewRequired: boolean) => Promise<void>;
    onReviewRequiredStateChange: (lineItemIds: number[], reviewRequired: boolean) => void;
    refreshLineItems: () => Promise<void>;
}

const getFilesForExpenseType = (expenseType: number | null, fileIdToLineItemsMap: Map<number, ILineItem[]>): number[] => {
    if (!expenseType || !fileIdToLineItemsMap) return [];

    if (expenseType === ExpenseType.MILEAGE && fileIdToLineItemsMap.has(MILEAGE_REVIEW_KEY)) {
        return [MILEAGE_REVIEW_KEY];
    }
    
    const files: number[] = [];
    const seenFiles = new Set<number>();
    
    fileIdToLineItemsMap.forEach((lineItems, fileId) => {
        if (seenFiles.has(fileId) || fileId === MILEAGE_REVIEW_KEY) return;
        
        const matchesExpenseType = lineItems.some(li => {
            if (expenseType === ExpenseType.TRANSPORTATION) {
                return li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE;
            }
            return li.expenseType === expenseType;
        });
        
        if (matchesExpenseType) {
            files.push(fileId);
            seenFiles.add(fileId);
        }
    });
    
    return files;
};

const getIndexInAllFiles = (targetFileId: number, allFileIds: number[]): number => {
    return allFileIds.indexOf(targetFileId);
};

const firstUnapprovedFileId = (
    orderedFileIds: number[],
    fileIdToLineItemsMap: Map<number, ILineItem[]>,
    approvedExpenseTypes: Set<number>
): number => {
    for (const fileId of orderedFileIds) {
        if (fileId === MILEAGE_REVIEW_KEY) {
            if (!approvedExpenseTypes.has(ExpenseType.MILEAGE)) {
                return fileId;
            }

            continue;
        }

        const items = fileIdToLineItemsMap.get(fileId) || [];

        if (items.length === 0) {
            continue;
        }

        const type = items[0].expenseType === ExpenseType.CAR_SERVICE
            ? ExpenseType.TRANSPORTATION
            : items[0].expenseType;

        if (!approvedExpenseTypes.has(type) && !approvedExpenseTypes.has(items[0].expenseType)) {
            return fileId;
        }
    }

    return orderedFileIds[0];
};

export const useSidebarState = (props: UseSidebarStateProps) => {
    const [selectedButton, setSelectedButton] = useState<FileDocumentType | null>(null);
    const [activeExpenseType, setActiveExpenseType] = useState<number | null>(null);
    const [approvedExpenseTypes, setApprovedExpenseTypes] = useState<Set<number>>(new Set());
    const [approvedLineItemIds, setApprovedLineItemIds] = useState<Set<number>>(new Set());
    const [activeExpenseTypeFileIndex, setActiveExpenseTypeFileIndex] = useState<Map<number, number>>(new Map());
    const [currentFileIdToLineItemsMap, setCurrentFileIdToLineItemsMap] = useState<Map<number, ILineItem[]>>(new Map());
    const [currentFileIds, setCurrentFileIds] = useState<number[]>([]);
    const [allFileIds, setAllFileIds] = useState<number[]>([]);
    const currentTripGroupIdRef = useRef<number | null>(null);
    const currentFileIdToLineItemsMapRef = useRef<Map<number, ILineItem[]>>(new Map());
    const allFileIdsRef = useRef<number[]>([]);
    const activeExpenseTypeRef = useRef<number | null>(null);

    const buildHandlers = useCallback((fileIdToLineItemsMap: Map<number, ILineItem[]>, _allFileIdsList: number[]) => {
        const onApproveHandler = props.onSetReviewRequiredBulk ? async (lineItemIds: number[]) => {
            await props.onSetReviewRequiredBulk!(lineItemIds, false);
            props.onReviewRequiredStateChange(lineItemIds, false);
            setApprovedLineItemIds(prev => {
                const next = new Set(prev);
                lineItemIds.forEach(id => next.add(id));
                return next;
            });
        } : undefined;
        const onRequestReviewHandler = props.onSetReviewRequiredBulk ? async (lineItemIds: number[]) => {
            await props.onSetReviewRequiredBulk!(lineItemIds, true);
            props.onReviewRequiredStateChange(lineItemIds, true);
            setApprovedLineItemIds(prev => {
                const next = new Set(prev);
                lineItemIds.forEach(id => next.delete(id));
                return next;
            });
        } : undefined;
        const handleExpenseTypeChange = async (newExpenseType: number | null, newFileIndex?: number) => {
            if (newExpenseType !== null && newFileIndex !== undefined) {
                setActiveExpenseTypeFileIndex(prev => {
                    const newMap = new Map(prev);
                    newMap.set(newExpenseType, newFileIndex);
                    return newMap;
                });
                activeExpenseTypeRef.current = newExpenseType;
                setActiveExpenseType(newExpenseType);
            } else if (newExpenseType !== null) {
                setActiveExpenseTypeFileIndex(prev => {
                    const newMap = new Map(prev);
                    if (newExpenseType !== activeExpenseTypeRef.current) {
                        newMap.set(newExpenseType, 0);
                    }
                    return newMap;
                });
                activeExpenseTypeRef.current = newExpenseType;
                setActiveExpenseType(newExpenseType);
            } else {
                activeExpenseTypeRef.current = null;
                setActiveExpenseType(null);
            }
        };
        const handleExpenseTypeApproved = async (expenseType: number) => {
            const allLineItems = Array.from(fileIdToLineItemsMap.values()).flat();
            const approvedType = expenseType === ExpenseType.CAR_SERVICE
                ? ExpenseType.TRANSPORTATION
                : expenseType;

            setApprovedLineItemIds(prev => {
                const next = new Set(prev);
                allLineItems.forEach(li => {
                    const liType = li.expenseType === ExpenseType.CAR_SERVICE
                        ? ExpenseType.TRANSPORTATION
                        : li.expenseType;

                    if (liType === approvedType) {
                        next.add(li.id);
                    }
                });
                return next;
            });

            setApprovedExpenseTypes(prev => {
                const next = new Set(prev);
                next.add(expenseType);

                if (approvedType !== expenseType) {
                    next.add(approvedType);
                }

                return next;
            });
        };
        const handleReviewRequiredStateChange = (lineItemIds: number[], reviewRequired: boolean) => {
            props.onReviewRequiredStateChange(lineItemIds, reviewRequired);
        };

        return {
            onApproveHandler,
            onRequestReviewHandler,
            handleExpenseTypeChange,
            handleExpenseTypeApproved,
            handleReviewRequiredStateChange
        };
    }, [props]);

    const updateSidebarForFile = useCallback(async (fileId: number, allFileIdsList: number[]) => {
        const fileIdToLineItemsMap = currentFileIdToLineItemsMapRef.current;
        const tripGroupId = currentTripGroupIdRef.current;
        const isMileageReview = fileId === MILEAGE_REVIEW_KEY;

        if ((!fileId && fileId !== MILEAGE_REVIEW_KEY) || !fileIdToLineItemsMap || !tripGroupId) {
            return;
        }

        const targetFileLineItems = fileIdToLineItemsMap.get(fileId) || [];

        if (targetFileLineItems.length === 0) {
            return;
        }

        const expenseType = isMileageReview ? ExpenseType.MILEAGE : targetFileLineItems[0].expenseType;
        const filesForExpenseType = getFilesForExpenseType(expenseType, fileIdToLineItemsMap);
        const expenseTypeFileIndex = filesForExpenseType.indexOf(fileId);

        if (expenseTypeFileIndex === -1) {
            return;
        }

        activeExpenseTypeRef.current = expenseType;
        setActiveExpenseType(expenseType);
        setActiveExpenseTypeFileIndex(prev => {
            const newMap = new Map(prev);
            newMap.set(expenseType, expenseTypeFileIndex);
            return newMap;
        });

        let fileUrl = '';
        if (!isMileageReview) {
            const fetchedUrls = await props.aggregateReceiptFiles(targetFileLineItems, tripGroupId, [fileId], true);
            fileUrl = fetchedUrls.get(fileId) || props.receiptFileUrls.get(fileId) || '';
            if (!fileUrl) {
                return;
            }
        }

        const handlers = buildHandlers(fileIdToLineItemsMap, allFileIdsList);
        const indexInAllFiles = getIndexInAllFiles(fileId, allFileIdsList);

        setCurrentFileIds(allFileIdsList);
        setSelectedButton(FileDocumentType.Receipts);

        props.onShowDataSideBar(
            FileDocumentType.Receipts,
            fileId,
            fileUrl,
            allFileIdsList.length,
            allFileIdsList,
            indexInAllFiles,
            fileIdToLineItemsMap,
            handlers.onApproveHandler,
            handlers.onRequestReviewHandler,
            handlers.handleExpenseTypeChange,
            handlers.handleExpenseTypeApproved,
            props.refreshLineItems,
            handlers.handleReviewRequiredStateChange
        );
    }, [props, buildHandlers]);

    const buildReviewFileList = (tripGroup: ITripGroup) => {
        const receiptLineItems = tripGroup.lineItems.filter(li => li.receiptFileId);
        const mileageLineItems = tripGroup.lineItems.filter(li => li.expenseType === ExpenseType.MILEAGE);
        const sortedLineItems = sortLineItemsBySummaryOrder(receiptLineItems);

        const fileIdToLineItemsMap = new Map<number, ILineItem[]>();
        const orderedFileIds: number[] = [];
        const seenFileIds = new Set<number>();

        sortedLineItems.forEach(li => {
            const fileId = li.receiptFileId!;
            if (!fileId) return;

            if (!seenFileIds.has(fileId)) {
                seenFileIds.add(fileId);
                orderedFileIds.push(fileId);
                fileIdToLineItemsMap.set(fileId, []);
            }
            fileIdToLineItemsMap.get(fileId)!.push(li);
        });

        const hasMileageToReview = sumMileageLineItemAmount(mileageLineItems) > 0
            || sumMileageRoundTripMiles(mileageLineItems) > 0;

        if (hasMileageToReview) {
            orderedFileIds.push(MILEAGE_REVIEW_KEY);
            fileIdToLineItemsMap.set(MILEAGE_REVIEW_KEY, mileageLineItems);
        }

        return { orderedFileIds, fileIdToLineItemsMap };
    };

    const openSidebar = useCallback(async (tripGroup: ITripGroup, _loadFirstOnly: boolean = true) => {
        if (!tripGroup || !tripGroup.lineItems) return;

        const { orderedFileIds, fileIdToLineItemsMap } = buildReviewFileList(tripGroup);
        if (orderedFileIds.length === 0) return;

        setAllFileIds(orderedFileIds);
        allFileIdsRef.current = orderedFileIds;
        setCurrentFileIdToLineItemsMap(fileIdToLineItemsMap);
        currentFileIdToLineItemsMapRef.current = fileIdToLineItemsMap;
        currentTripGroupIdRef.current = tripGroup.id;

        const startFileId = firstUnapprovedFileId(orderedFileIds, fileIdToLineItemsMap, approvedExpenseTypes);

        await updateSidebarForFile(startFileId, orderedFileIds);
    }, [updateSidebarForFile, approvedExpenseTypes]);

    const openMileageReview = useCallback(async (tripGroup: ITripGroup) => {
        if (!tripGroup || !tripGroup.lineItems) return;

        const { orderedFileIds, fileIdToLineItemsMap } = buildReviewFileList(tripGroup);
        if (!orderedFileIds.includes(MILEAGE_REVIEW_KEY)) return;

        setAllFileIds(orderedFileIds);
        allFileIdsRef.current = orderedFileIds;
        setCurrentFileIdToLineItemsMap(fileIdToLineItemsMap);
        currentFileIdToLineItemsMapRef.current = fileIdToLineItemsMap;
        currentTripGroupIdRef.current = tripGroup.id;

        await updateSidebarForFile(MILEAGE_REVIEW_KEY, orderedFileIds);
    }, [updateSidebarForFile]);

    const closeSidebar = useCallback(() => {
        setSelectedButton(null);
        setActiveExpenseType(null);
    }, []);

    return {
        selectedButton,
        activeExpenseType,
        approvedExpenseTypes,
        approvedLineItemIds,
        activeExpenseTypeFileIndex,
        openSidebar,
        openMileageReview,
        closeSidebar,
        setSelectedButton,
        setActiveExpenseType,
        setApprovedExpenseTypes,
        setApprovedLineItemIds,
        setActiveExpenseTypeFileIndex
    };
};

